import {Alignment, Material, Object3D, WonderlandEngine} from '@wonderlandengine/api';
import {
    Align,
    Display,
    FlexDirection,
    Justify,
    PositionType,
    Wrap,
    YogaNodeProps,
} from '@wonderlandengine/react-ui';
import {
    Panel,
    Text,
    Button,
    Container,
    PanelProps,
} from '@wonderlandengine/react-ui/components';
import React, {useRef, useEffect, useState} from 'react';
import {Character} from './register-character.js';
import {purchases} from 'uber-sdk/purchases.js';
import {Vrm} from '@wonderlandengine/components';

export const CustomProgressBar = (
    props: React.PropsWithChildren<
        YogaNodeProps & {
            /* Number between 0-1 */
            value: number;
            rounding?: number;

            fgMaterial?: Material;
            bgMaterial?: Material;

            fgColor?: string | Float32Array | number;
            bgColor?: string | Float32Array | number;
        }
    >
) => {
    const rounding = props.rounding ?? 30;
    const value = Math.min(1, Math.max(0, props.value)); // Clamping value between 0 and 1

    return (
        <Panel
            // material={props.bgMaterial}
            backgroundColor={props.bgColor}
            {...props}
            resolution={6}
            rounding={rounding * 1.5}
        >
            <Container
                alignItems={Align.FlexStart}
                position={PositionType.Absolute}
                width="100%"
                height="100%"
            >
                {props.children}
            </Container>
            {value > 0.05 && (
                <Panel
                    width={`${100 * value}%`} // Adjusted width calculation
                    height="100%"
                    //material={props.fgMaterial}
                    backgroundColor={props.fgColor}
                    rounding={rounding * 1.5}
                    resolution={6}
                    left={0} // Removed left offset
                ></Panel>
            )}
        </Panel>
    );
};

const CharacterTag = (
    props: React.PropsWithChildren<
        YogaNodeProps & {
            padding?: number;
            fgColor: string | Float32Array | number;
            textValue?: string;
        }
    >
) => {
    const rounding = 14;
    return (
        <Panel
            backgroundColor={props.fgColor}
            rounding={rounding}
            padding={props.padding}
            position={PositionType.Relative}
        >
            <Text fontSize={14} color="#ffffffff" textAlign="center">
                {props.textValue}
            </Text>
        </Panel>
    );
};

const CharacterDisplay = (
    props: React.PropsWithChildren<
        YogaNodeProps & {
            name?: string;
            globalIndex: number;
            character: Character;
        }
    >
) => {
    const ref = useRef<Object3D | null>(null);
    const [created, setCreated] = useState(false);
    useEffect(() => {
        /* Characters not loaded yet? */
        if (ref.current === null) return;
        if (created) return;
        setCreated(true);

        const obj = ref.current;

        const v = props.character;
        const engine: WonderlandEngine = obj.engine;

        const meshCount = (v.body?.length ?? 0) + (v.head?.length ?? 0);
        if (meshCount === 0) return;
        const [head, body] = engine.scene.addObjects(2);

        v.body?.forEach((c: any) => {
            if (c.mesh != undefined) {
                body.addComponent('mesh', c);
            }
        });
        v.head?.forEach((c: any) => {
            if (c.mesh != undefined) {
                head.addComponent('mesh', c);
            }
        });

        let s = 0.5;
        head.parent = obj;
        body.parent = head;

        head.scaleLocal([s, s, s]);
        head.setPositionLocal([0.5, -0.65, 0.05]);
        body.setPositionLocal([0.0, -0.8, 0.0]);
    });

    return (
        <Container top={90} alignContent={Align.Center} minWidth="100%">
            <Text fontSize={12} color="#000000" textAlign="center">
                {props.name}
            </Text>
            <Container ref={ref} />
        </Container>
    );
};

interface CharacterButtonProps {
    character: any;
    globalIndex: number;
    selectedCharacter: number;
    clickCallback: (event: any, index: number, character: any) => void;
    comp: any;
    profile: any;
}

const CharacterButton = React.forwardRef<
    Object3D,
    React.PropsWithChildren<YogaNodeProps & CharacterButtonProps>
>((props, ref) => {
    const {character, globalIndex, selectedCharacter, clickCallback, comp, profile} = props;
    const purchasesEnabled = purchases.hasProviders();
    const isBuyable = purchasesEnabled && character.price !== 0;
    const purchasedItem =
        isBuyable && purchases.isItemPurchased('avatar-' + character.name.toLowerCase());
    const itemUnlocked =
        (isBuyable && purchasedItem) ||
        (!isBuyable && (character.level ?? 0) <= profile.getLevel());

    const tagColor = isBuyable ? '#ffd649' : '#008000';
    const tagText = isBuyable ? `${character.price} Coins` : `Level ${character.level}`;

    return (
        <Button
            key={globalIndex}
            ref={ref}
            width="15%" // Ensures 6 columns fit within the row
            minHeight={180}
            rounding={30}
            onClick={(event) => clickCallback(event, globalIndex, character)}
            hovered={{
                material:
                    selectedCharacter === globalIndex
                        ? comp.panelActiveMat
                        : comp.panelHoverMat,
            }}
            material={
                selectedCharacter === globalIndex ? comp.panelActiveMat : comp.panelMat
            }
            justifyContent={Justify.FlexStart}
            alignItems={Align.Center}
        >
            <Container top={-70}>
                <CharacterDisplay
                    name={character.name}
                    globalIndex={globalIndex}
                    character={character}
                />
                {!itemUnlocked && (
                    <Container top={45}>
                        <CharacterTag padding={7} fgColor={tagColor} textValue={tagText} />
                    </Container>
                )}
            </Container>
        </Button>
    );
});

const UnknownCharacterButton = React.forwardRef(
    (
        props: React.PropsWithChildren<
            YogaNodeProps & {
                globalIndex: number;
                comp: any;
            }
        >,
        ref
    ) => {
        const {globalIndex, comp} = props;

        return (
            <Button
                key={globalIndex}
                ref={ref} // FIXME: Forward ref properly in Button
                width="15%" // Ensures 6 columns fit within the row
                minHeight={180}
                rounding={30}
                material={comp.panelMat}
                justifyContent={Justify.FlexStart}
                alignItems={Align.Center}
            >
                <Text fontSize={40} color="#000000" top={60}>
                    ?
                </Text>
            </Button>
        );
    }
);

type CharacterGridProps = React.PropsWithChildren<
    YogaNodeProps & {
        characters: Character[];
        selectedCharacter: number;
        clickCallback: (event: any, globalIndex: number, character: Character) => void;
        comp: any;
        profile: any;
    }
>;

export const CharacterGrid: React.FC<CharacterGridProps> = ({
    characters,
    selectedCharacter,
    clickCallback,
    comp,
    profile,
}) => {
    return (
        <Container
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            gap={12}
            flexWrap={Wrap.Wrap}
        >
            {characters.map((character, i) => (
                <CharacterButton
                    key={i}
                    character={character}
                    globalIndex={i}
                    selectedCharacter={selectedCharacter}
                    clickCallback={clickCallback}
                    comp={comp}
                    profile={profile}
                />
            ))}

            <UnknownCharacterButton key={16} globalIndex={16} comp={comp} />
        </Container>
    );
};

export const Modal = (
    props: React.PropsWithChildren<
        {
            /* User clicked outside of modal */
            onCancel: () => void;
        } & PanelProps
    >
) => {
    return (
        <Container
            key="modal"
            position={PositionType.Absolute}
            top={0}
            left={0}
            width="100%"
            height="100%"
            display={Display.Flex}
            alignContent={Align.Center}
            justifyContent={Justify.Center}
            onClick={props.onCancel}
            z={0.6}
        >
            <Panel alignSelf={Align.Center} {...props}>
                {props.children}
            </Panel>
        </Container>
    );
};

export const ViverseAvatar = React.forwardRef<
    Object3D,
    React.PropsWithChildren<YogaNodeProps & {url: string}>
>((props, ref) => {
    const {url} = props;

    const r = useRef<Object3D | null>(null);
    useEffect(() => {
        /* Characters not loaded yet? */
        if (r && r.current === null) return;

        const obj = r.current;
        if (obj == null) return;

        const c = obj.children[0] ?? obj.addChild();

        const vrm = c.getComponent('vrm');
        if (vrm && 'src' in vrm && vrm.src === url) return;
        vrm?.destroy();
        c.addComponent('vrm', {src: url});
        c.setPositionLocal([0.0, -6, -4.0]);
        c.setScalingWorld([1.0, 1.0, 1.0]);
        c.rotateAxisAngleDegObject([0.0, 1.0, 0.0], 90);
    }, [url]);

    return (
        <Container
            top={90}
            height={500}
            justifyContent={Justify.Center}
            alignContent={Align.Center}
            minWidth="100%"
        >
            <Container width={1} ref={r} marginLeft="auto" marginRight="auto" />
        </Container>
    );
});
